import google from "@images/google-clean.png";
import okta from "@images/okta.jpeg";
import placeholder from "@images/placeholder.png";

import { CreateIntegrationData, createQueryAction, Integration } from "../api";
import GoogleIntegrationDetails from "../components/GoogleIntegrationDetails.tsx";
import HTTPIntegrationSettings from "../components/HTTPIntegrationsSettings";
import { IntegrationBasicDetails } from "../components/IntegrationBasicDetails";
import NativeIntegrationDetails from "../components/OktaIntegrationDetails";
import { googleQueryActions } from "../native-integrations/google.ts";
import { OktaQueryActions } from "../native-integrations/okta.ts";
import { TypeDefinition } from "./common";
import { IntegrationMasterForm } from "./form-inputs";

export enum IntegrationType {
  Custom = "Custom",
  Google = "Google",
  LocalTransform = "LocalTransform",
  Okta = "Okta",
}

export const isIntegrationType = (value: unknown): value is IntegrationType =>
  typeof value === "string" &&
  Object.values(IntegrationType).includes(value as IntegrationType);

export type IntegrationDefinition = {
  afterCreateIntegration?: (integration: Integration) => Promise<void>;
  transformFormData: (
    data: IntegrationMasterForm,
  ) => CreateIntegrationData["body"];
} & TypeDefinition;

export const integrations: Record<IntegrationType, IntegrationDefinition> = {
  Custom: {
    description: "Custom Integration",
    icon: placeholder,
    name: "Custom",
    steps: [
      {
        component: <IntegrationBasicDetails nameLabel="Connection" />,
        nextLabel: "Next Step",
        title: "Integration Setup",
      },
      {
        component: <HTTPIntegrationSettings />,
        nextLabel: "Save Integration",
        title: "API Setup",
      },
    ],
    title: "Custom Integration",
    transformFormData: (data: IntegrationMasterForm) => {
      let authenticationDetails:
        | {
            authenticationDetails: {
              apiKey: string;
              apiKeyName: string;
              mappingType: "Header" | "QueryParameter";
            };
            authenticationType: "APIKey";
          }
        | {
            authenticationDetails: {
              password: string;
              userName: string;
            };
            authenticationType: "Basic";
          }
        | {
            authenticationType: "None";
          };

      if (data.authenticationType === "Basic") {
        authenticationDetails = {
          authenticationDetails: {
            password: data.password,
            userName: data.username,
          },
          authenticationType: "Basic",
        };
      } else if (data.authenticationType === "APIKey") {
        authenticationDetails = {
          authenticationDetails: {
            apiKey: data.apiKey,
            apiKeyName: data.apiKeyName,
            mappingType: data.apiKeyParameterType,
          },
          authenticationType: "APIKey",
        };
      } else {
        authenticationDetails = {
          authenticationType: "None",
        };
      }

      return {
        apiParameters: data.httpParameters,
        authenticationDetails,
        baseUrl: data.baseUrl,
        description: data.description,
        iconUrl: data.iconUrl,
        integrationDetails: {
          connectionName: data.connectionName,
        },
        name: data.connectionName,
        type: "Custom",
      };
    },
  },
  Google: {
    afterCreateIntegration: async (integration: Integration) => {
      await Promise.all(
        googleQueryActions.map(async (action) => {
          await createQueryAction({
            body: {
              ...action,
              integrationId: integration.id,
            },
          });
        }),
      );
    },
    description: "Google Workspace Integration",
    icon: google,
    name: "Google",
    steps: [
      {
        component: (
          <IntegrationBasicDetails allowIcon={false} nameLabel="Connection" />
        ),
        nextLabel: "Next Step",
        title: "Integration Setup",
      },
      {
        component: <GoogleIntegrationDetails />,
        nextLabel: "Save Integration",
        title: "API Setup",
      },
    ],
    title: "Google Integration",
    transformFormData: (data: IntegrationMasterForm) => {
      return {
        apiParameters: [],
        authenticationDetails: {
          authenticationType: "None",
        },
        baseUrl: "https://admin.googleapis.com/admin/reports/v1",
        description: data.description,
        integrationDetails: {
          clientEmail: data.googleClientEmail,
          delegatedAdminEmail: data.googleDelegatedAdminEmail,
          privateKey: data.googlePrivateKey,
          privateKeyId: data.googlePrivateKeyId,
        },
        name: data.connectionName,
        type: "Google",
      };
    },
  },
  LocalTransform: {
    description: "Custom Local Transformation",
    icon: placeholder,
    name: "Local Transformation",
    steps: [
      {
        component: (
          <IntegrationBasicDetails nameLabel="Transformation Collection" />
        ),
        nextLabel: "Save Collection",
        title: "Transformation Collection Setup",
      },
    ],
    title: "Local Data Transformation",

    transformFormData: (data: IntegrationMasterForm) => {
      return {
        apiParameters: data.httpParameters,
        authenticationDetails: {
          authenticationType: "None",
        },
        baseUrl: "",
        description: data.description,
        iconUrl: data.iconUrl,
        name: data.connectionName,
        type: "LocalTransform",
      };
    },
  },
  Okta: {
    afterCreateIntegration: async (integration: Integration) => {
      await Promise.all(
        OktaQueryActions.map(async (action) => {
          await createQueryAction({
            body: {
              ...action,
              integrationId: integration.id,
            },
          });
        }),
      );
    },
    description: "Okta Integration",
    icon: okta,
    name: "Okta",
    steps: [
      {
        component: (
          <IntegrationBasicDetails allowIcon={false} nameLabel="Connection" />
        ),
        nextLabel: "Next Step",
        title: "Integration Setup",
      },
      {
        component: <NativeIntegrationDetails />,
        nextLabel: "Save Integration",
        title: "API Setup",
      },
    ],
    title: "Okta Integration",
    transformFormData: (data: IntegrationMasterForm) => {
      return {
        apiParameters: [],
        authenticationDetails: {
          authenticationDetails: {
            apiKey: data.apiKey,
            apiKeyName: "Authorization",
            mappingType: "Header",
          },
          authenticationType: "APIKey",
        },
        baseUrl: data.baseUrl,
        description: data.description,
        iconUrl: "/images/okta.jpeg",
        integrationDetails: {
          connectionName: data.connectionName,
        },
        name: data.connectionName,
        type: "Custom",
      };
    },
  },
};
